import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { authService } from '../services/auth.service';
import notificationWebSocket from '../services/notification-websocket.service';
import { notificationInbox } from '../services/notification-inbox.service';
import type { Notification, NotificationPayload, SubscriptionPayload, NotificationEventType } from '../types/notifications';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  connectionId: string | null;
  deliveryError: string | null;
  addNotification: (payload: NotificationPayload) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  removeNotification: (id: string) => void;
  subscribe: (subscription: SubscriptionPayload) => void;
  unsubscribe: () => void;
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
const MAX_NOTIFICATIONS = 100;
const utcDate = (value: string) => new Date(/(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : value + 'Z');

// Get notification icon based on event type
export const getNotificationIcon = (eventType: NotificationEventType): string => {
  switch (eventType) {
    case 'motion':
      return '🚶';
    case 'lpr':
      return '🚗';
    case 'intrusion':
      return '⚠️';
    case 'entry_exit':
      return '🅿️';
    case 'camera_alert':
      return '📹';
    case 'system_alert':
      return '🔔';
    default:
      return '📬';
  }
};

// Get notification color based on priority
export const getNotificationColor = (priority: string): string => {
  switch (priority) {
    case 'urgent':
      return 'red';
    case 'high':
      return 'orange';
    case 'normal':
      return 'blue';
    case 'low':
      return 'gray';
    default:
      return 'blue';
  }
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const [inboxHealthy, setInboxHealthy] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const seen = useRef(new Set<string>());
  const primed = useRef(false);
  const scope = useRef('');

  const addNotification = useCallback((payload: NotificationPayload) => {
    const id = payload.id || `event_${payload.event_id || payload.data?.event_id || ''}_${payload.timestamp}_${payload.title}`;
    if (seen.current.has(id)) return;
    seen.current.add(id);
    if (seen.current.size > 500) seen.current.delete(seen.current.values().next().value!);
    setNotifications(previous => [{ ...payload, id, read: payload.read || false, createdAt: utcDate(payload.timestamp) }, ...previous]
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, MAX_NOTIFICATIONS));
    if (primed.current) {
      if (payload.priority === 'urgent' || payload.priority === 'high') playNotificationSound();
      showBrowserNotification(payload);
    }
  }, []);

  useEffect(() => {
    const currentScope = `${user.companyId}:${user.userId}`;
    scope.current = currentScope;
    seen.current.clear(); primed.current = false;
    setNotifications([]); setInboxHealthy(false); setDeliveryError(null);
    setSocketConnected(false); setConnectionId(null);
    if (!isAuthenticated) { notificationWebSocket.disconnect(); return; }
    let active = true;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const offConnection = notificationWebSocket.onConnectionChange(connected => {
      if (active) { setSocketConnected(connected); setConnectionId(connected ? notificationWebSocket.getConnectionId() : null); }
    });
    const offNotification = notificationWebSocket.onNotification(payload => { if (active) addNotification(payload); });
    const token = authService.getToken();
    if (token) notificationWebSocket.connect(token);
    const poll = async () => {
      const started = new Date();
      try {
        const page = await notificationInbox.list(0, true, controller.signal);
        if (!active) return;
        const latestToken = authService.getToken();
        if (latestToken) notificationWebSocket.connect(latestToken);
        // Bootstrap silently; a refresh must not replay sounds for old alerts.
        [...page.items].reverse().forEach(addNotification);
        setNotifications(current => {
          const items = page.items.map(item => ({ ...item, id: item.id!, read: item.read || false, createdAt: utcDate(item.timestamp) }));
          const ids = new Set(items.map(item => item.id));
          // Keep live frames received after this snapshot began. Otherwise the
          // server is authoritative, including dismissals made in another tab.
          const newer = current.filter(item => !ids.has(item.id) && ((item.sequence_id || 0) > page.cursor || item.createdAt > started));
          return [...items, ...newer].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, MAX_NOTIFICATIONS);
        });
        primed.current = true; setInboxHealthy(true); setDeliveryError(null);
      } catch { if (active) { setInboxHealthy(false); setDeliveryError('Notifications could not sync. Retrying automatically.'); } }
      finally { if (active) timer = setTimeout(poll, 15000); }
    };
    void poll();
    return () => { active = false; controller.abort(); clearTimeout(timer); offConnection(); offNotification(); notificationWebSocket.disconnect(); };
  }, [isAuthenticated, user.companyId, user.userId, addNotification]);

  const persist = (action: Promise<unknown>, update: () => void) => {
    const currentScope = scope.current;
    void action.then(() => { if (currentScope === scope.current) update(); }).catch(() => {
      if (currentScope === scope.current) setDeliveryError('Could not save notification changes. Please try again.');
    });
  };
  // Page subscriptions no longer own or narrow the global notification channel.
  const subscribe = useCallback((_subscription: SubscriptionPayload) => {}, []);
  const unsubscribe = useCallback(() => {}, []);
  const value: NotificationContextType = {
    notifications, unreadCount: notifications.filter(item => !item.read).length,
    isConnected: socketConnected || inboxHealthy, connectionId, deliveryError, addNotification,
    markAsRead: id => persist(notificationInbox.read(id), () => setNotifications(current => current.map(item => item.id === id ? { ...item, read: true } : item))),
    markAllAsRead: () => persist(notificationInbox.readAll(), () => setNotifications(current => current.map(item => ({ ...item, read: true })))),
    clearNotifications: () => persist(notificationInbox.dismissAll(), () => setNotifications([])),
    removeNotification: id => persist(notificationInbox.dismiss(id), () => setNotifications(current => current.filter(item => item.id !== id))),
    subscribe, unsubscribe,
  };
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within a NotificationProvider');
  return context;
};

// Play notification sound
const playNotificationSound = () => {
  try {
    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880; // A5 note
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    oscillator.onended = () => { void audioContext.close(); };
  } catch (e) {
    // Ignore sound errors
  }
};

// Show browser notification
const showBrowserNotification = (payload: NotificationPayload) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const icon = getNotificationIcon(payload.event_type);
    new Notification(payload.title, {
      body: payload.body,
      icon: `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>${icon}</text></svg>`,
      tag: payload.id || payload.event_id?.toString(),
      requireInteraction: payload.priority === 'urgent',
    });
  }
};

// Request browser notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

export default NotificationContext;
